import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { workReports as api, getSubscription, duplicateReport, emailReport, reportPdfUrl, apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PageHeader, Card, EmptyState, Loading, ConfirmDelete } from "@/components/Shared";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileBarChart2, Plus, Eye, Pencil, Copy, FileDown, Mail, Trash2, Lock } from "lucide-react";

const RPT_STATUS = { draft: "draft", in_progress: "pending", completed: "accepted", sent: "sent_status" };

export default function WorkReports() {
  const nav = useNavigate();
  const [list, setList] = useState(null);
  const [access, setAccess] = useState(true);

  const load = () => Promise.all([getSubscription(), api.list()]).then(([s, l]) => { setAccess(s.report_access); setList(l); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const dup = async (id) => { try { const r = await duplicateReport(id); toast.success(`Duplicated as ${r.number}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const mail = async (id) => { try { const r = await emailReport(id); toast.success(r.message); load(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (id) => { try { await api.remove(id); toast.success("Report deleted"); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!list) return <Loading />;

  if (!access) {
    return (
      <div>
        <PageHeader title="Work Reports" subtitle="Create professional assessment reports with findings, photographs, conclusions and recommendations." />
        <EmptyState icon={Lock} title="Professional Reports are available on Business and Pro"
          description="Upgrade your plan to create branded leak-detection and assessment reports with photographs and PDF export."
          action={<div className="flex gap-2 justify-center"><Button onClick={() => nav("/billing")} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="upgrade-business">Upgrade to Business</Button><Button variant="outline" onClick={() => nav("/billing")} data-testid="view-plans">View Plans</Button></div>} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Work Reports" subtitle="Create professional assessment reports with findings, photographs, conclusions and recommendations.">
        <Button onClick={() => nav("/work-reports/new")} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="new-report-button"><Plus className="w-4 h-4 mr-1.5" /> New Report</Button>
      </PageHeader>

      {list.length === 0 ? (
        <EmptyState icon={FileBarChart2} title="No reports yet" description="Create your first professional assessment report."
          action={<Button onClick={() => nav("/work-reports/new")} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="empty-new-report"><Plus className="w-4 h-4 mr-1.5" /> New Report</Button>} />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-100">
                <th className="px-5 py-3 font-semibold">Report</th>
                <th className="px-5 py-3 font-semibold hidden md:table-cell">Customer</th>
                <th className="px-5 py-3 font-semibold hidden lg:table-cell">Property</th>
                <th className="px-5 py-3 font-semibold hidden md:table-cell">Assessment</th>
                <th className="px-5 py-3 font-semibold text-center">Status</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50" data-testid={`report-row-${r.id}`}>
                  <td className="px-5 py-3.5"><button onClick={() => nav(`/work-reports/${r.id}`)} className="font-semibold text-[#0B132B] hover:text-[#D4AF37]">{r.number}</button><div className="text-xs text-slate-400">{fmtDate(r.created_at)}</div></td>
                  <td className="px-5 py-3.5 hidden md:table-cell text-slate-600">{r.customer?.name || "—"}</td>
                  <td className="px-5 py-3.5 hidden lg:table-cell text-slate-600 max-w-[200px] truncate">{r.property_address || "—"}</td>
                  <td className="px-5 py-3.5 hidden md:table-cell text-slate-600">{fmtDate(r.assessment_date)}</td>
                  <td className="px-5 py-3.5 text-center"><StatusBadge status={RPT_STATUS[r.status] || "draft"} testId={`report-status-${r.id}`} /></td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-0.5">
                      <Button variant="ghost" size="icon" onClick={() => nav(`/work-reports/${r.id}`)} data-testid={`view-report-${r.id}`}><Eye className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => nav(`/work-reports/${r.id}/edit`)} data-testid={`edit-report-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => dup(r.id)} data-testid={`dup-report-${r.id}`}><Copy className="w-4 h-4" /></Button>
                      <a href={reportPdfUrl(r.id)} target="_blank" rel="noreferrer"><Button variant="ghost" size="icon" data-testid={`pdf-report-${r.id}`}><FileDown className="w-4 h-4" /></Button></a>
                      <Button variant="ghost" size="icon" onClick={() => mail(r.id)} data-testid={`email-report-${r.id}`}><Mail className="w-4 h-4" /></Button>
                      <ConfirmDelete onConfirm={() => remove(r.id)} title={`Delete report ${r.number}?`} trigger={<Button variant="ghost" size="icon" className="text-rose-500" data-testid={`delete-report-${r.id}`}><Trash2 className="w-4 h-4" /></Button>} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
